import {
  format,
} from 'd3-format';
import { FluentBundle } from 'fluent';
import { GetString } from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import {
  CountryLookback,
  CountryProfile,
  GrowthProjectionClassification,
  GrowthProjectionRelativeToIncome,
  Location,
  NewProductsCountry,
  ProductClass,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { formatPercentage } from '../../numberFormatters';
import { getCountryName, growthProjectionsYear } from '../../Utils';
import {
  numberToSign,
} from '../Utils';
import { primaryColor1 } from '../Utils';
import { defaultECIProductClass } from '../Utils';
import GlossaryTerm, { TooltipAlign, TooltipType } from './GlossaryTerm';
import Localized from './Localized';
import {
  leftPanelZIndices,
} from './Utils';

//#region styling
const Root = styled.div`
  border-top: 1px solid #78787f;
  padding-top: 1rem;
  z-index: ${leftPanelZIndices.text};
  position: relative;
`;
const Paragraph = styled.div`
  color: #333;
  line-height: 1.444;
  font-size: 2vh;
  margin-bottom: 1rem;

  @media (min-height: 750px) and (min-width: 1000px) {
    font-size: 1.1vw;
  }
`;

const Disclaimer = styled.div`
  line-height: 1.4;
  color: #888;
  font-size: 0.75rem;

  a {
    color: ${primaryColor1};

    &:hover {
      border-bottom: 1px solid ${primaryColor1};
    }
  }

  @media (min-width: 1200px) {
    font-size: 1vw;
  }
`;
//#endregion

const query = gql`
  query LandingPageText($id: ID!, $productClass: ProductClass!, $year: Int!) {
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryProfile(location: $id) {
      coiClassification
      latestGdpPerCapita {
        year
        quantity
      }
      latestGdpPerCapitaPpp {
        year
        quantity
      }
      latestPopulation {
        year
        quantity
      }
      latestGdpPerCapitaRank {
        year
        quantity
      }
      latestEciRank
      growthProjectionRelativeToIncome
      growthProjectionClassification
      growthProjection
      growthProjectionPercentileClassification
      incomeClassification
    }
    countryLookback(
      id: $id,
      productClass: $productClass,
      gdpPerCapitaChangeConstantCagrYearRange: FiveYears,
      gdpPcConstantCagrRegionalDifferenceYearRange: FiveYears,
      eciRankChangeYearRange: TenYears,
      diversityChangeYearRange: TenYears,
    ) {
      gdpPerCapitaChangeConstantCagr
      gdpPcConstantCagrRegionalDifference
      eciRankChange
      diversityChange
    }
    newProductsCountry(location: $id, year: $year) {
      newProductCount
    }
  }
`;

interface SuccessResponse {
  location: null | {
    id: Location['id']
    shortName: Location['shortName']
    nameAbbr: Location['nameAbbr']
    thePrefix: Location['thePrefix'],
  };
  countryProfile: null | {
    coiClassification: CountryProfile['coiClassification']
    latestGdpPerCapita: CountryProfile['latestGdpPerCapita']
    latestGdpPerCapitaPpp: CountryProfile['latestGdpPerCapitaPpp']
    latestGdpPerCapitaRank: CountryProfile['latestGdpPerCapitaRank']
    latestPopulation: CountryProfile['latestPopulation']
    latestEciRank: CountryProfile['latestEciRank']
    growthProjectionRelativeToIncome: CountryProfile['growthProjectionRelativeToIncome']
    growthProjectionClassification: CountryProfile['growthProjectionClassification']
    growthProjection: CountryProfile['growthProjection']
    growthProjectionPercentileClassification: CountryProfile['growthProjectionPercentileClassification'],
    incomeClassification: CountryProfile['incomeClassification'],
  };
  countryLookback: null | {
    gdpPerCapitaChangeConstantCagr: CountryLookback['gdpPerCapitaChangeConstantCagr']
    gdpPcConstantCagrRegionalDifference: CountryLookback['gdpPcConstantCagrRegionalDifference']
    eciRankChange: CountryLookback['eciRankChange']
    diversityChange: CountryLookback['diversityChange'],
  };
  newProductsCountry: null | {
    newProductCount: NewProductsCountry['newProductCount'],
  };
}

interface Variables {
  id: string;
  productClass: ProductClass;
  year: number;
}

interface Props {
  countryId: string;
  getFluentString: GetString;
  fluentBundle: FluentBundle;
  zIndex: number;
  year: number;
}

export const spellOutPopulationNumber = (input: number) => {
  const withSISuffix = format('.3~s')(input);

  const removeSuffix = (withSuffix: string) => withSuffix.slice(0, withSuffix.length - 1);

  let result: string;
  if (withSISuffix.match(/\d$/) !== null) {
    // This means the number is less than 1000 so there's no SI suffix:
    result = withSISuffix;
  } else if (withSISuffix.endsWith('k')) {
    result = `${removeSuffix(withSISuffix)} thousand`;
  } else if (withSISuffix.endsWith('M')) {
    result = `${removeSuffix(withSISuffix)} million`;
  } else if (withSISuffix.endsWith('G')) {
    result = `${removeSuffix(withSISuffix)} billion`;
  } else {
    throw new Error('No country should have a population as large as ' + input);
  }
  return result;
};

const formatGDPPerCapita = format('$,');

const Text = (props: Props) => {
  const {
    countryId, getFluentString, fluentBundle, zIndex, year,
  } = props;
  const variables: Variables = {
    id: countryId,
    productClass: defaultECIProductClass,
    year,
  };
  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables: variables, errorPolicy: 'all'});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    // console.error(error)
    const {
      location,
      countryProfile,
      countryLookback,
      newProductsCountry,
    } = data;
    if (
        location === null ||
        countryProfile === null ||
        countryLookback === null ||
        newProductsCountry === null) {
      output = null;
    } else {

      
      const {
        shortName,
        nameAbbr,
        thePrefix,
      } = location;
      const {
        coiClassification,
        latestGdpPerCapita,
        latestGdpPerCapitaRank,
        latestGdpPerCapitaPpp,
        latestPopulation,
        latestEciRank,
        growthProjectionRelativeToIncome,
        growthProjectionClassification,
        growthProjection,
        growthProjectionPercentileClassification,
        incomeClassification,
      } = countryProfile;
      const {
        gdpPerCapitaChangeConstantCagr,
        gdpPcConstantCagrRegionalDifference,
        eciRankChange,
        diversityChange,
      } = countryLookback;
      const {
        newProductCount,
      } = newProductsCountry;
      const {namePrimary, nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      enum EnoughNewProducts {
        Enough = 'Enough',
        TooFew = 'TooFew',
      }

      const enoughNewProducts = !newProductCount || newProductCount < 10
        ? EnoughNewProducts.TooFew : EnoughNewProducts.Enough;
      let gdpPerCapitaPPPPhrase: string;
      if (latestGdpPerCapitaPpp && latestGdpPerCapitaPpp.quantity) {

        gdpPerCapitaPPPPhrase = getFluentString('landing-text-para-1-gdp-ppp-normal', {
          'gdp-per-capita-ppp': formatGDPPerCapita(latestGdpPerCapitaPpp.quantity),
          'gdp-per-capita-ppp-year': latestGdpPerCapitaPpp.year.toString(),
        });
      } else if (latestGdpPerCapita !== null) {

        gdpPerCapitaPPPPhrase = getFluentString('landing-text-para-1-gdp-ppp-backup', {
          'gdp-per-capita-year': latestGdpPerCapita.year.toString(),
        });

      } else  {

        gdpPerCapitaPPPPhrase = '';
      }
      if ( latestGdpPerCapita === null || latestGdpPerCapitaRank === null ||
          latestPopulation === null || growthProjection === null ||
          latestEciRank === null || growthProjectionPercentileClassification === null) {
        output = null;
        

      } else {
        const { quantity: gdpPerCapita}  = latestGdpPerCapita;
        const { quantity: gdpPerCapitaRank } = latestGdpPerCapitaRank;
        const populationSpelledOut = spellOutPopulationNumber(latestPopulation.quantity);
        const gdpChangePercent = gdpPerCapitaChangeConstantCagr !== null ?
                                    formatPercentage(gdpPerCapitaChangeConstantCagr, 1) : 'N/A';

        const getGrowthProjectionConjunctiveAdverb = (complexityRelativeIncome: GrowthProjectionRelativeToIncome | null, growthClassification: GrowthProjectionClassification | null): string => {
          if(complexityRelativeIncome === 'More' && growthClassification === 'slow') {
            return 'However';
          } else if(complexityRelativeIncome === 'Less' && growthClassification === 'rapid') {
            return 'However';
          } else if(complexityRelativeIncome === 'ModeratelyMore' && growthClassification === 'slow') {
            return 'NoConjunctiveAdverb';
          } else if(complexityRelativeIncome === 'ModeratelyLess' && growthClassification === 'rapid') {
            return 'NoConjunctiveAdverb';
          } else if(complexityRelativeIncome === 'Same') {
            return 'NoConjunctiveAdverb';
          } else {
            return 'AsResult';
          }
        };
          
        const paragraph1Text = getFluentString('landing-text-para-1', {
          'country-name': namePrimary,
          'income-classification': incomeClassification,
          'income-rank': gdpPerCapitaRank,
          'population-spelled-out': populationSpelledOut,
          'gdp-per-capita': formatGDPPerCapita(gdpPerCapita),
          'gdp-per-capita-ppp-phrase': gdpPerCapitaPPPPhrase,
          'gdp-change-magnitude-as-percentage': gdpChangePercent,
          'gdp-change-compared-to-region': gdpPcConstantCagrRegionalDifference,
        });
        const paragraph2Args = {
          'country-name': nameSecondary,
          'eciGlossaryTerm': (
            <GlossaryTerm
              type={TooltipType.Standard}
              term={getFluentString('glossary-eci-term-name')}
              explanation={getFluentString('glossary-eci-term-definition')}
              citation={undefined}
              align={TooltipAlign.Standard}
              attachment={null}
            />
          ),
          'eci-rank': latestEciRank,
          'eci-change-sign': numberToSign(eciRankChange),
          'eci-rank-change-sign': numberToSign(eciRankChange),
          'eci-rank-change-magnitude': Math.abs(eciRankChange),
          'diversity-change-sign': numberToSign(diversityChange),
          'coi-classification': coiClassification,
          'enough-new-products': enoughNewProducts,
        };
        const paragraph3Text = getFluentString('landing-text-para-3', {
          'country-name': nameSecondary,
          'expected-complexity-compared-to-income': growthProjectionRelativeToIncome,
          'growth-projection-classification': growthProjectionClassification,
          'growth-projection-conjunctive-adverb': getGrowthProjectionConjunctiveAdverb(growthProjectionRelativeToIncome, growthProjectionClassification),
          'growth-projection-as-percentage': formatPercentage(growthProjection, 1),
          'growth-projection-percentile-classification': growthProjectionPercentileClassification,
          'current-year-projection': growthProjectionsYear.toString(),
        });
        output = (
          <>
            <Paragraph>{paragraph1Text}</Paragraph>
            <Localized
              bundle={fluentBundle}
              messageId='landing-text-para-2'
              messageArgs={paragraph2Args}
            >
              <Paragraph/>
            </Localized>
            <Paragraph>{paragraph3Text}</Paragraph>
            <Disclaimer
              dangerouslySetInnerHTML={{__html: getFluentString('landing-text-data-disclaimer')}}
            />
          </>
        );
      }

    }
  } else {
    output = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <Root style={style}>
      {output}
    </Root>
  );
};

export default Text;
